<script>
  export let inputs;
  export let orientation = "horizontal";
  export let flag = null;
  export let link;
  export let hints = null;
  export let question = null;
  export let show_confetti = false;

  import { confettiWin } from "../helpers/confetti.js";
  import { tick } from "svelte";

  import { toast } from "@zerodevx/svelte-toast";

  import { ci, debug } from "../store";

  import Text from "./Text.svelte";
  import Button from "./Button.svelte";

  let el;

  //import { PuzzleText } from "./puzzle/PuzzleText.svelte";
  //import { PuzzleSelect } from "./puzzle/PuzzleSelect.svelte";

  /*const types = {
    text: PuzzleText,
    select: PuzzleSelect,
  };*/

  // add value attribute to every input
  for (let input of inputs) {
    input.value = "";
  }

  let puzzleFinished = false;

  $: inputInvalid = inputs.find((x) => {
    let valid = true;
    // length based
    if (x.type == "text") {
      if (x.amount && x.amount.hasOwnProperty("min")) {
        if (x.value.length < x.amount.min) return true;
      } else {
        if (x.value.length <= 0) return true;
      }
    }

    if (x.type == "select") {
      if (x.value.length <= 0) return true;
    }

    return false;
  });

  import { flags } from "../store.js";
  // https://svelte.dev/repl/a551a40e90374fd2861255c957123af2?version=3.22.2
  const addFlag = (flag) => {
    $flags.add(flag);
    $flags = $flags;
  };

  let hintLevel = 1;
  let hintToast = null;

  function showHintLevel(hint) {
    let hintText = hint <= 2 ? `Hinweis ${hint}` : "Lösung";
    let text = `<strong>${hintText}</strong><p>` + hints[hint - 1] + "</p>";

    //toast.pop(hintToast);

    hintToast = toast.push(text, {
      initial: 0,
      progress: 0,
    });

    if (hintLevel <= hint) {
      hintLevel = hint;
      hintLevel++;
    }
  }

  let validation = {
    text: (value, solution, caseSensitive = true) => {
      if (!caseSensitive)
        return (
          value.toLowerCase().replace(/\s/g, "") ===
          solution.toLowerCase().replace(/\s/g, "")
        );
      return value === solution;
    },
    select: (value, solution) => {
      return value.toLowerCase() === solution.toLowerCase();
    },
  };

  function submit() {
    let correct = true;

    for (let input of inputs) {
      let valid;
      switch (input.type) {
        case "text":
          // default is case sensitivity
          let caseSensitive = input.hasOwnProperty("case_sensitive")
            ? input.case_sensitive
            : true;

          if (Array.isArray(input.solution)) {
            // multiple solutions
            for (let solution of input.solution) {
              valid = validation[input.type](
                input.value,
                solution,
                caseSensitive
              );
              if (valid) break;
            }
          } else {
            // single solution
            valid = validation[input.type](
              input.value,
              input.solution,
              caseSensitive
            );
          }
          break;
        case "select":
          if (Array.isArray(input.solution)) {
            // multiple solutions
            for (let solution of input.solution) {
              valid = validation[input.type](input.value, solution);
              if (valid) break;
            }
          } else {
            // single solution
            valid = validation[input.type](input.value, input.solution);
          }
          break;
      }

      if (!valid) {
        correct = false;
        break;
      }
    }

    puzzleFeedback(correct);
  }

  async function puzzleFeedback(isCorrect) {
    if (isCorrect) {
      toast.pop(0);
      toast.push(
        `<h3>${
          $ci.strings.puzzle_feedback_correct ?? "Die Antwort war korrekt."
        }</h3>`
      );
      if (flag) addFlag(flag);
      if (link) window.open(link, "_blank").focus();
      puzzleFinished = true;

      if (show_confetti) {
        await tick();
        confettiWin();
      }
    } else {
      toast.push(
        `<h3>${
          $ci.strings.puzzle_feedback_incorrect ??
          "Die Antwort war nicht korrekt."
        }</h3>`
      );
    }
  }

  const labelKeypress = (e) => {
    if (e.charCode == 13 && !inputInvalid) submit();
  };
</script>

{#if question}
  <div class="locked-puzzle-question">
    <Text content={question} style="font-style: italic;" />
  </div>
{/if}
{#if $debug}
  <button
    class="locked-puzzle-debug-solve"
    disabled={puzzleFinished}
    on:click|preventDefault={() => puzzleFeedback(true)}>Lösen (Debug)</button
  >
{/if}
<div
  class="locked-puzzle"
  class:vertical={orientation === "vertical"}
  class:horizontal={orientation === "horizontal"}
>
  {#each inputs as { type, value, amount={}, options={}, label="" }}
    {#if type == "text"}
      <label class="locked-puzzle-label"
        >{label}
        <input
          type="text"
          bind:value
          minlength={amount.min ?? ""}
          maxlength={amount.max ?? ""}
          size={amount.max ?? ""}
          on:keypress={labelKeypress}
          disabled={puzzleFinished}
        /></label
      >
    {:else if (type = "select")}
      <label class="locked-puzzle-label"
        >{label}
        <select bind:value class="locked-puzzle-select">
          <option value="" selected disabled hidden>Auswählen...</option>
          {#each options as option}
            <option value={option}>{option}</option>
          {/each}
        </select>
      </label>
    {/if}
  {/each}
</div>
<br />
<div class="toast-anchor" bind:this={el} />
<Button disabled={inputInvalid || puzzleFinished} onClick={submit}
  >Absenden</Button
>
{#if hints}
  <div class="locked-puzzle-hint-container">
    <span class="locked-puzzle-hint" on:click={() => showHintLevel(1)}
      >{$ci.strings.hint ?? "Hinweis"} {1}</span
    >
    {#if hintLevel > 1}
      <span class="locked-puzzle-hint" on:click={() => showHintLevel(2)}
        >{$ci.strings.hint ?? "Hinweis"} {2}</span
      >
    {/if}
    {#if hintLevel > 2}
      <span class="locked-puzzle-hint" on:click={() => showHintLevel(3)}
        >{$ci.strings.hint_show_solution ?? "Lösung anzeigen"}</span
      >
    {/if}
  </div>
{/if}

<style>
  .locked-puzzle-label {
    display: inline-block;
    white-space: pre;
    flex: 1;
  }

  .locked-puzzle-hint {
    display: inline;
    cursor: pointer;
  }

  .locked-puzzle {
    display: inline-flex;
    justify-content: center;
    flex-wrap: wrap;
  }

  .locked-puzzle.vertical {
    flex-direction: column;
    align-items: flex-end;
  }

  .locked-puzzle-select {
    min-width: 10rem;
  }

  .locked-puzzle-hint-container {
    display: flex;
    flex-direction: column;
    justify-content: top;
    min-height: 4rem;
  }

  .locked-puzzle-debug-solve {
    position: absolute;
    top: 0;
    left: 0;
  }
</style>
